// src/encounter/care-plan-tab/care-plan-weeks.tsx
// The demo's "12-week care plan" panel, from the plan's activities (B1): the header line (programme, plan start, 12 weeks,
// retests, "x of y signed", Show all lanes / Fold to your lanes), the lane x week grid (lane tile + name + owner, W1–W12
// with the current week when the plan start says it, item bars in the lane's hue with their status and Review / Sign,
// retest diamonds with a "Same week" count), and the legend + footer line.
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { TEXT_ROLE_CLASSES } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { CARE_PLAN_LANE_IDS, type CarePlanActivityView, type CarePlanLane, type CarePlanView } from '../lib/care-plan-view';
import type { ActivityAction } from './use-activity-action';
import { activityActionFor, drawnWeeks, barId, formatPlanDate, laneTally, packBars, retestMarks, type LaneFold, type RetestMark } from './care-plan-board';
import {
  ACTIVITY_STATUS_LABEL,
  CARE_PLAN_LANE_ICON,
  CARE_PLAN_LANE_LABEL,
  CARE_PLAN_LANE_LEGEND,
  CARE_PLAN_LANE_LOOK,
  CP_BODY_STRONG,
  CP_CARD_TITLE,
  CP_LABEL,
  CP_LINK_ACTION,
  CP_META,
  PLAN_WEEKS,
  weeksText,
} from './care-plan-labels';
import { ActivityActionButton, ActivityStateLine, AiDraftChip, CarePlanStateGlyph, LaneIconTile } from './care-plan-marks';

/** Grid placement on the 12 weeks. Literal class names, so the styles are generated (no inline style). */
const COL_START: Record<number, string> = {
  1: 'col-start-1', 2: 'col-start-2', 3: 'col-start-3', 4: 'col-start-4', 5: 'col-start-5', 6: 'col-start-6',
  7: 'col-start-7', 8: 'col-start-8', 9: 'col-start-9', 10: 'col-start-10', 11: 'col-start-11', 12: 'col-start-12',
};
const COL_END: Record<number, string> = {
  2: 'col-end-2', 3: 'col-end-3', 4: 'col-end-4', 5: 'col-end-5', 6: 'col-end-6', 7: 'col-end-7',
  8: 'col-end-8', 9: 'col-end-9', 10: 'col-end-10', 11: 'col-end-11', 12: 'col-end-12', 13: 'col-end-13',
};
const ROW_START: Record<number, string> = {
  1: 'row-start-1', 2: 'row-start-2', 3: 'row-start-3', 4: 'row-start-4', 5: 'row-start-5', 6: 'row-start-6',
  7: 'row-start-7', 8: 'row-start-8', 9: 'row-start-9', 10: 'row-start-10', 11: 'row-start-11', 12: 'row-start-12',
};
const row = (n: number) => ROW_START[Math.min(Math.max(1, n), 12)];

const META_INHERIT = cn(TEXT_ROLE_CLASSES.meta, 'text-inherit');
const LABEL_INHERIT = cn(CP_LABEL, 'text-inherit');

interface ItemProps {
  carePlanId: string;
  busy: boolean;
  onAction?: (a: CarePlanActivityView, kind: ActivityAction) => void;
}

/** One bar: the lane's tint for accepted / signed, an outline for a draft (the demo's _He). */
function WeekBar({ activity: a, showOwner, carePlanId, busy, onAction }: ItemProps & { activity: CarePlanActivityView; showOwner: boolean }) {
  const look = CARE_PLAN_LANE_LOOK[a.lane];
  const draft = a.status === 'ai-draft' || a.status === 'draft';
  const kind = activityActionFor(a);
  const Glyph = CARE_PLAN_LANE_ICON[a.lane];
  // The bar can be drawn wider than its weeks (room for its words), so its own weeks are always written on it.
  const extra = [weeksText(a), showOwner ? a.ownerRole : null].filter(Boolean);
  return (
    <div
      id={barId(carePlanId, a.id)}
      tabIndex={-1}
      data-block={a.id}
      data-status={a.status}
      title={`${a.title} · ${weeksText(a)}${a.ownerRole ? ` · ${a.ownerRole}` : ''} · ${ACTIVITY_STATUS_LABEL[a.status]}`}
      className={cn(
        'flex h-10 w-full min-w-0 items-center gap-2 overflow-hidden whitespace-nowrap rounded-md border pl-2 pr-1',
        draft ? cn('bg-transparent text-ink-secondary', look.draft) : look.bar,
      )}
    >
      <span aria-hidden className={cn('grid h-6 w-6 min-w-0 shrink place-items-center overflow-hidden rounded-sm', draft ? 'text-ink-meta' : look.ink)}>
        <Glyph size={14} strokeWidth={2} aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col items-start overflow-hidden text-left">
        <span className={cn(LABEL_INHERIT, 'max-w-full truncate')}>{a.title}</span>
        <span className={cn(META_INHERIT, 'flex max-w-full items-center gap-1 truncate')}>
          <ActivityStateLine activity={a} />
          {extra.length ? <span>· {extra.join(' · ')}</span> : null}
        </span>
      </span>
      {onAction && kind ? (
        <span className="ml-auto flex shrink-0">
          <ActivityActionButton kind={kind} busy={busy} onConfirm={() => onAction(a, kind)} />
        </span>
      ) : null}
    </div>
  );
}

const DIAMOND_STATE: Record<RetestMark['state'], string> = {
  signed: 'border-brand-primary bg-brand-primary',
  accepted: 'border-brand-primary bg-surface-read',
  draft: 'border-grey-700 bg-surface-read',
  'ai-draft': 'border-grey-700 bg-surface-read',
};

/** The "Same week" count: a small accent-edged pill (the demo's badge). */
function SameWeekCount({ count, className }: { count: number; className?: string }) {
  return (
    <span
      data-count-badge
      className={cn(
        'grid h-4 min-w-4 place-items-center rounded-md border border-accent-primary bg-surface-read px-1 text-xs font-medium leading-none tabular-nums text-accent-active-text',
        className,
      )}
    >
      {count}
    </span>
  );
}

/** A retest (or a week's retests): a diamond on its week, the label for one, a count for several (the demo's yHe). */
function RetestDiamond({ mark, open, onToggle }: { mark: RetestMark; open: boolean; onToggle: () => void }) {
  return (
    <span className="relative flex h-12 items-center justify-center">
      <button
        type="button"
        aria-expanded={open}
        aria-label={`${mark.label || `${mark.items.length} items this week`}${mark.past ? ` · after W${PLAN_WEEKS}` : ''}`}
        data-milestone={mark.label || `cluster-${mark.items.length}`}
        data-retest-ids={mark.items.map((i) => i.id).join(' ')}
        onClick={onToggle}
        className="relative grid size-6 place-items-center rounded-sm hover:bg-accent-active-bg"
      >
        <span aria-hidden data-diamond className={cn('block size-3 rotate-45 rounded-xs border-2', DIAMOND_STATE[mark.state])} />
        {mark.items.length > 1 ? <SameWeekCount count={mark.items.length} className="absolute -right-2 -top-1.5" /> : null}
      </button>
      {mark.label ? (
        <span
          className={cn(
            CP_LABEL,
            'pointer-events-none absolute left-full flex items-center gap-1 whitespace-nowrap',
            mark.state === 'draft' || mark.state === 'ai-draft' ? 'text-ink-secondary' : 'text-accent-active-text',
          )}
        >
          {mark.state === 'ai-draft' ? <AiDraftChip /> : null}
          {mark.label}
          {mark.past ? ` · after W${PLAN_WEEKS}` : ''}
        </span>
      ) : null}
    </span>
  );
}

/** The opened retest mark: its items, each with its status and Review / Sign (the demo opens a popover). */
function RetestItems({ mark, ...item }: ItemProps & { mark: RetestMark }) {
  return (
    <ul aria-label={mark.past ? `Retests after W${PLAN_WEEKS}` : `W${mark.week} retests`} className="m-0 flex list-none flex-col divide-y divide-surface-line rounded-md border border-surface-line bg-surface-card p-0 px-3">
      {mark.items.map((a) => {
        const kind = activityActionFor(a);
        return (
          <li key={a.id} data-activity-id={a.id} data-status={a.status} className="flex items-center gap-3 py-2">
            <span className="flex min-w-0 flex-1 flex-col">
              <span className={CP_BODY_STRONG}>{a.title}</span>
              <span className={cn(CP_META, 'flex items-center gap-1')}>
                <ActivityStateLine activity={a} /> · {weeksText(a)}
              </span>
            </span>
            {item.onAction && kind ? (
              <ActivityActionButton kind={kind} busy={item.busy} onConfirm={() => item.onAction?.(a, kind)} />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** The 12 week columns behind a lane: a line per week, past weeks shaded, the current week's line (the demo's today). */
function WeekColumns({ week }: { week: number | null }) {
  return (
    <div aria-hidden className="absolute inset-0 grid grid-cols-12">
      {Array.from({ length: PLAN_WEEKS }, (_, i) => i + 1).map((w) => (
        <div key={w} className={cn('flex justify-center border-l border-surface-line', week !== null && w < week && 'bg-surface-app')}>
          {w === week ? <span data-today className="h-full w-px bg-accent-primary" /> : null}
        </div>
      ))}
    </div>
  );
}

function LaneRow({
  lane,
  items,
  owner,
  folded,
  onFold,
  week,
  ...item
}: ItemProps & {
  lane: CarePlanLane;
  items: CarePlanActivityView[];
  /** The lane's one owner (all its items share it); null = each bar names its own. */
  owner: string | null;
  folded: boolean;
  onFold: (fold: boolean) => void;
  week: number | null;
}) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const bars = items.filter((a) => a.kind !== 'retest');
  const marks = retestMarks(items.filter((a) => a.kind === 'retest'));
  const packed = packBars(bars);
  const markRow = bars.length ? packed.tracks + 1 : 1;
  // The marks after W12 sit on W12 too: when W12 has its own mark, they take the row below it.
  const pastBelow = marks.some((m) => m.past) && marks.some((m) => !m.past && m.week === PLAN_WEEKS);
  const markRowOf = (m: RetestMark) => (m.past && pastBelow ? markRow + 1 : markRow);
  const open = marks.find((m) => m.key === openKey) ?? null;
  const label = CARE_PLAN_LANE_LABEL[lane];
  return (
    <div role="group" aria-label={label} data-lane={lane} data-folded={folded || undefined} className="flex border-b border-surface-line">
      <div className="relative w-64 shrink-0 pr-3">
        <span aria-hidden data-lane-stripe className={cn('absolute inset-y-1.5 left-0 rounded-full border-l-3', CARE_PLAN_LANE_LOOK[lane].rule)} />
        <div className="flex h-14 items-center gap-3 pl-3">
          <LaneIconTile lane={lane} size="lane" />
          <span className="min-w-0 flex-1">
            <span className={cn(CP_BODY_STRONG, 'block')}>{label}</span>
            {owner ? <span className={cn(CP_META, 'block')}>{owner}</span> : null}
          </span>
          {items.length > 0 ? (
            <button
              type="button"
              data-lane-toggle={lane}
              aria-expanded={!folded}
              aria-label={`${folded ? 'Show' : 'Fold'} ${label}`}
              title={`${folded ? 'Show' : 'Fold'} ${label}`}
              onClick={() => onFold(!folded)}
              className="grid size-6 shrink-0 place-items-center rounded-sm text-ink-secondary hover:bg-accent-active-bg hover:text-accent-active-text"
            >
              {folded ? <ChevronRight size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
            </button>
          ) : null}
        </div>
      </div>
      <div className="relative min-w-0 flex-1">
        <WeekColumns week={week} />
        {folded ? (
          <div className="relative flex h-14 items-center pl-2">
            <div data-lane-summary={lane} className={cn(CP_META, 'flex items-center gap-2 rounded-md border border-surface-line bg-surface-read py-1 pl-3 pr-1')}>
              <span className="tabular-nums">{laneTally(items)}</span>
              <button type="button" onClick={() => onFold(false)} aria-label={`Show the ${items.length} items in ${label}`} className={CP_LINK_ACTION}>
                Show
              </button>
            </div>
          </div>
        ) : items.length === 0 ? (
          <span data-lane-empty={lane} className={cn(CP_META, 'relative flex h-14 items-center pl-3 text-ink-meta')}>
            No plan items yet
          </span>
        ) : (
          <ul className="relative m-0 grid list-none grid-cols-12 p-0 py-0.5">
            {bars.map((a) => {
              const { from, to } = drawnWeeks(a);
              return (
                <li
                  key={a.id}
                  data-activity-id={a.id}
                  data-status={a.status}
                  className={cn(COL_START[from], COL_END[to + 1], row((packed.track.get(a.id) ?? 0) + 1), 'flex h-12 min-w-0 items-center px-0.5')}
                >
                  <WeekBar activity={a} showOwner={!owner} {...item} />
                </li>
              );
            })}
            {marks.map((m) => (
              <li key={m.key} className={cn(COL_START[m.week], COL_END[m.week + 1], row(markRowOf(m)))}>
                <RetestDiamond mark={m} open={openKey === m.key} onToggle={() => setOpenKey(openKey === m.key ? null : m.key)} />
              </li>
            ))}
            {open ? (
              <li className={cn('col-span-12 col-start-1 pb-2', row(markRow + (pastBelow ? 2 : 1)))}>
                <RetestItems mark={open} {...item} />
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </div>
  );
}

function Dot() {
  return <span aria-hidden>·</span>;
}

/** The demo's legend (DQ): order types, retest, same week, then the three states and the footer line. */
function PlanBoardLegend() {
  return (
    <footer className={cn(CP_META, 'mt-3 flex flex-col gap-2 border-t border-surface-line pt-2')}>
      <div data-lane-legend className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className={cn(CP_LABEL, 'whitespace-nowrap text-ink-meta')}>Order type</span>
        {CARE_PLAN_LANE_IDS.map((l) => (
          <span key={l} className="inline-flex items-center gap-2">
            <LaneIconTile lane={l} size="small" />
            {CARE_PLAN_LANE_LEGEND[l]}
          </span>
        ))}
        <span className="inline-flex items-center gap-2">
          <span aria-hidden className="inline-block size-3 rotate-45 rounded-xs border-2 border-accent-primary bg-surface-read" />
          Retest or review
        </span>
        <span className="inline-flex items-center gap-2">
          <SameWeekCount count={2} />
          Same week
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <div data-state-legend className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="inline-flex items-center gap-2">
            <AiDraftChip />
            awaiting review
          </span>
          <span className="inline-flex items-center gap-2">
            <CarePlanStateGlyph kind="half" />
            Accepted, unsigned
          </span>
          <span className="inline-flex items-center gap-2">
            <CarePlanStateGlyph kind="done" />
            Signed
          </span>
        </div>
        <span>Orders: physician only · nothing happens until signed</span>
      </div>
    </footer>
  );
}

export interface PlanWeeksPanelProps {
  plan: CarePlanView;
  /** The lanes the viewer owns items in: the others fold by default ("Fold to your lanes"). [] = nothing folds. */
  myLanes: readonly CarePlanLane[];
  /** The plan week today falls in; null = not known (no plan start), so no week is marked. */
  week: number | null;
  /** The signed-in user's name, for a lane the viewer owns. */
  viewerName: string | null;
  viewerUserId: string | null;
  busy: boolean;
  onAction?: (a: CarePlanActivityView, kind: ActivityAction) => void;
  actionError?: string | null;
  /** A lane the viewer folded or opened; a lane not in it takes its default. Held by the board, so "Open" can unfold. */
  fold: LaneFold;
  onFoldChange: (fold: LaneFold) => void;
}

export function PlanWeeksPanel({ plan, myLanes, week, viewerName, viewerUserId, busy, onAction, actionError, fold, onFoldChange: setFold }: PlanWeeksPanelProps) {
  const activities = plan.activities ?? [];
  const itemsOf = (l: CarePlanLane) => activities.filter((a) => a.lane === l);
  const foldedByDefault = (l: CarePlanLane) => myLanes.length > 0 && !myLanes.includes(l) && itemsOf(l).length > 0;
  const isFolded = (l: CarePlanLane) => (fold[l] ?? foldedByDefault(l)) && itemsOf(l).length > 0;
  const foldedCount = CARE_PLAN_LANE_IDS.filter(isFolded).length;
  const signed = activities.filter((a) => a.status === 'signed').length;
  const retests = activities.filter((a) => a.kind === 'retest').length;
  const ownerOf = (items: CarePlanActivityView[]): string | null => {
    if (items.length === 0) return null;
    if (viewerUserId && viewerName && items.every((a) => a.ownerUserId === viewerUserId)) return viewerName;
    const roles = new Set(items.map((a) => a.ownerRole ?? ''));
    return roles.size === 1 ? items[0].ownerRole : null;
  };
  return (
    <section aria-label="12-week care plan" data-plan-weeks className="min-h-0 rounded-lg bg-surface-panel p-4">
      <header className="min-w-0">
        <h2 className={CP_CARD_TITLE}>{PLAN_WEEKS}-week care plan</h2>
        <div data-plan-summary className={cn(CP_META, 'mt-1 flex flex-wrap items-center gap-x-2 tabular-nums')}>
          <span>{plan.title}</span>
          {plan.status !== 'unknown' ? (
            <>
              <Dot />
              <span>Status: {plan.status}</span>
            </>
          ) : null}
          <Dot />
          <span>{plan.period?.start ? `Plan start ${formatPlanDate(plan.period.start)}` : 'Plan start: not signed yet'}</span>
          {plan.period?.end ? (
            <>
              <Dot />
              <span>Ends {formatPlanDate(plan.period.end)}</span>
            </>
          ) : null}
          {plan.currentStep.state === 'known' ? (
            <>
              <Dot />
              <span>Step: {plan.currentStep.name}</span>
            </>
          ) : null}
          <Dot />
          <span>{PLAN_WEEKS} weeks</span>
          {plan.activities ? (
            <>
              <Dot />
              <span>{retests} retests</span>
              <Dot />
              <span className="inline-flex items-center gap-2">
                <CarePlanStateGlyph kind={activities.length > 0 && signed === activities.length ? 'done' : signed > 0 ? 'half' : 'empty'} />
                <span data-signed-total>
                  {signed} of {activities.length} signed
                </span>
              </span>
            </>
          ) : null}
          {myLanes.length > 0 ? (
            <>
              <Dot />
              <button
                type="button"
                data-lanes-all={foldedCount ? 'show' : 'fold'}
                onClick={() => setFold(foldedCount ? Object.fromEntries(CARE_PLAN_LANE_IDS.map((l) => [l, false])) : {})}
                className={cn(CP_LINK_ACTION, 'h-auto px-1')}
              >
                {foldedCount ? `Show all ${CARE_PLAN_LANE_IDS.length} lanes` : 'Fold to your lanes'}
              </button>
            </>
          ) : null}
        </div>
      </header>
      {plan.activities === undefined ? <p className={cn(CP_META, 'm-0 mt-2')}>This environment does not record plan items yet.</p> : null}
      <div className="mt-2 flex min-w-0">
        <div data-board-label className={cn(CP_LABEL, 'flex h-9 w-64 shrink-0 items-end whitespace-nowrap border-b border-surface-line pb-2 text-ink-meta')}>
          Lane
        </div>
        <div className="grid h-9 min-w-0 flex-1 grid-cols-12 items-end border-b border-surface-line">
          {Array.from({ length: PLAN_WEEKS }, (_, i) => i + 1).map((w) => (
            <span
              key={w}
              data-week={w}
              data-current-week={w === week || undefined}
              className={cn(CP_LABEL, 'pb-2 text-center tabular-nums', w === week ? 'text-accent-active-text' : 'text-ink-meta')}
            >
              W{w}
            </span>
          ))}
        </div>
      </div>
      {CARE_PLAN_LANE_IDS.map((lane) => {
        const items = itemsOf(lane);
        return (
          <LaneRow
            key={lane}
            lane={lane}
            items={items}
            owner={ownerOf(items)}
            folded={isFolded(lane)}
            onFold={(f) => setFold({ ...fold, [lane]: f })}
            week={week}
            carePlanId={plan.carePlanId}
            busy={busy}
            onAction={onAction}
          />
        );
      })}
      {actionError ? (
        <p role="alert" className={cn(CP_META, 'm-0 mt-2')}>
          {actionError}
        </p>
      ) : null}
      <PlanBoardLegend />
    </section>
  );
}
